import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Card } from '@/components/ui/Card';
import { useCreateUser, type UserRole } from '@/api/hooks/useUsers';
import { ApiException } from '@/api/client';

export function UserNewPage() {
  const navigate = useNavigate();
  const create = useCreateUser();
  const [userEmail, setUserEmail] = useState('');
  const [userAlias, setUserAlias] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('internal_user');
  const [teamsText, setTeamsText] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!userEmail) errs.user_email = 'Required';
    if (!/^[^@]+@[^@]+$/.test(userEmail)) errs.user_email = 'Invalid email';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitError(null);
    const teams = teamsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    create.mutate(
      {
        user_email: userEmail,
        user_alias: userAlias || undefined,
        user_role: userRole,
        teams: teams.length ? teams : undefined,
        max_budget: maxBudget ? Number(maxBudget) : undefined,
      },
      {
        onSuccess: (data) => {
          const userId = (data as { user_id?: string }).user_id;
          if (userId) navigate(`/users/${encodeURIComponent(userId)}`);
          else navigate('/users');
        },
        onError: (e) =>
          setSubmitError(e instanceof ApiException ? e.message : 'Create failed'),
      },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Create User</h1>
      <Card>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {submitError && (
            <div
              style={{
                padding: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          )}
          <FormField label="Email" htmlFor="user_email" error={errors.user_email}>
            <Input
              id="user_email"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="alice@example.com"
            />
          </FormField>
          <FormField label="Alias (optional)" htmlFor="user_alias">
            <Input
              id="user_alias"
              value={userAlias}
              onChange={(e) => setUserAlias(e.target.value)}
            />
          </FormField>
          <FormField label="Role" htmlFor="user_role">
            <Select
              id="user_role"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value as UserRole)}
              options={[
                { value: 'internal_user', label: 'Internal User' },
                { value: 'internal_user_viewer', label: 'Internal User (Viewer)' },
                { value: 'proxy_admin', label: 'Proxy Admin' },
                { value: 'proxy_admin_viewer', label: 'Proxy Admin (Viewer)' },
              ]}
            />
          </FormField>
          <FormField
            label="Teams (optional)"
            htmlFor="teams"
            hint="Comma or newline separated team IDs"
          >
            <Input
              id="teams"
              value={teamsText}
              onChange={(e) => setTeamsText(e.target.value)}
              placeholder="team-a, team-b"
            />
          </FormField>
          <FormField label="Max Budget (optional)" htmlFor="max_budget">
            <Input
              id="max_budget"
              type="number"
              value={maxBudget}
              onChange={(e) => setMaxBudget(e.target.value)}
              placeholder="100"
            />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => navigate('/users')}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}